import { memo } from 'react';
import cn from 'classnames';
import type {
  TableAlign,
  TableCellProps,
  TableEmptyProps,
  TableHeaderCellProps,
  TableProps,
  TableRowProps,
  TableSectionProps,
} from './types';
import s from './index.module.scss';

export function Table({
  children,
  containerClassName,
  containerStyle,
  minWidth = 720,
  maxHeight,
  radius = 20,
  stickyHeader = true,
  withRowDividers = true,
  className,
  style,
  ...tableProps
}: TableProps) {
  const isSticky = stickyHeader || maxHeight !== undefined;

  return (
    <div
      className={cn(s.container, containerClassName)}
      data-slot="table-container"
      data-sticky-header={isSticky || undefined}
      data-with-dividers={withRowDividers || undefined}
      style={{
        borderRadius: radius,
        ...(maxHeight !== undefined && { maxHeight, overflowY: 'auto' }),
        ...containerStyle,
      }}
    >
      <table
        {...tableProps}
        className={cn(s.table, className)}
        data-slot="table"
        style={{ minWidth, ...style }}
      >
        {children}
      </table>
    </div>
  );
}

function TableHeader({ children, ...props }: TableSectionProps) {
  return (
    <thead {...props} data-slot="table-header">
      {children}
    </thead>
  );
}

function TableBody({ children, ...props }: TableSectionProps) {
  return (
    <tbody {...props} data-slot="table-body">
      {children}
    </tbody>
  );
}

const TableRow = memo(function TableRow({
  children,
  interactive = false,
  selected = false,
  className,
  style,
  onClick,
  ...props
}: TableRowProps) {
  const isInteractive = interactive || typeof onClick === 'function';

  return (
    <tr
      {...props}
      className={cn(s.row, className)}
      data-slot="table-row"
      data-interactive={isInteractive || undefined}
      data-selected={selected || undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-selected={selected || undefined}
      onClick={onClick}
      onKeyDown={isInteractive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.currentTarget.click();
        }
      } : undefined}
      style={style}
    >
      {children}
    </tr>
  );
});

function TableHeaderCell({
  children,
  align,
  width,
  scope = 'col',
  className,
  style,
  ...props
}: TableHeaderCellProps) {
  const cellAlign: TableAlign = align ?? 'start';
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <th
      {...props}
      scope={scope}
      className={cn(s.headerCell, className)}
      data-align={cellAlign}
      data-slot="table-header-cell"
      style={{ width: resolvedWidth, ...style }}
    >
      {children}
    </th>
  );
}

function TableCell({
  children,
  align,
  className,
  style,
  ...props
}: TableCellProps) {
  const cellAlign: TableAlign = align ?? 'start';

  return (
    <td
      {...props}
      className={cn(s.cell, className)}
      data-align={cellAlign}
      data-slot="table-cell"
      style={style}
    >
      {children}
    </td>
  );
}

function TableEmpty({ children, className }: TableEmptyProps) {
  return (
    <tbody>
      <tr>
        <td colSpan={1000} className={cn(s.emptyCell, className)}>
          {children}
        </td>
      </tr>
    </tbody>
  );
}

export namespace Table {
  export const Header = TableHeader;
  export const Body = TableBody;
  export const Row = TableRow;
  export const HeaderCell = TableHeaderCell;
  export const Cell = TableCell;
  export const Empty = TableEmpty;
}
